import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useAuthStore } from "../../stores/authStore";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../services/supabase";
import { useToast } from "../../components/ui/Toast";
import Sidebar from "../../components/layout/Sidebar";
import VideoUploadModal from "../../components/ui/video/VideoUploadModal";

interface RimInventoryItem {
  id: string;
  business_id: string | null;
  brand: string;
  model: string;
  size: string;
  width: number | null;
  diameter: number | null;
  rim_offset: number | null;
  bolt_pattern: string | null;
  pcd: number | null;
  center_bore: number | null;
  finish: string | null;
  material: string | null;
  price: number;
  quantity: number;
  image_url: string | null;
  video_url?: string | null;
  videos?: string[] | null;
  gallery: string[] | null;
  features: string[] | null;
  description: string | null;
  created_at: string | null;
  updated_at: string | null;
}

const RimInventoryPage: React.FC = () => {
  const [inventory, setInventory] = useState<RimInventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoTargetItem, setVideoTargetItem] = useState<RimInventoryItem | null>(null);
  
  // Form state
  const [formData, setFormData] = useState<Partial<RimInventoryItem>>({
    brand: "",
    model: "",
    size: "",
    width: null,
    diameter: null,
    rim_offset: null,
    bolt_pattern: "",
    pcd: null,
    center_bore: null,
    finish: "",
    material: "Alloy",
    price: 0,
    quantity: 0,
    image_url: "",
    features: [],
    description: "",
  });

  const { user, profile } = useAuthStore();
  const navigate = useNavigate();
  const { success: showSuccessToast, error: showErrorToast } = useToast();

  // Fetch inventory
  useEffect(() => {
    if (!user || !profile?.id) {
      navigate("/auth/login");
      return;
    }

    fetchInventory();
  }, [user, profile]);

  const fetchInventory = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get business ID for the current user
      if (!profile?.id) return;
      
      const { data: businessData, error: businessError } = await supabase
        .from("businesses")
        .select("id")
        .eq("owner_id", profile.id)
        .single();

      if (businessError || !businessData) {
        setError("Business not found. Please complete your business profile.");
        setLoading(false);
        return;
      }

      // Fetch rim inventory
      const { data, error: fetchError } = await supabase
        .from("rim_inventory")
        .select("*")
        .eq("business_id", businessData.id)
        .order("created_at", { ascending: false });

      if (fetchError) {
        setError("Failed to load rim inventory");
        console.error("Rim inventory fetch error:", fetchError);
      } else {
        setInventory(data || []);
      }
    } catch (err) {
      setError("An unexpected error occurred");
      console.error("Rim inventory fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle number inputs
  const handleNumberChange = (name: string, value: string) => {
    const numValue = value === "" ? null : parseFloat(value);
    setFormData(prev => ({
      ...prev,
      [name]: numValue,
    }));
  };

  // Handle features (comma-separated input)
  const handleFeaturesChange = (value: string) => {
    const featuresArray = value.split(',').map(f => f.trim()).filter(f => f !== '');
    setFormData(prev => ({
      ...prev,
      features: featuresArray,
    }));
  };

  // Add new rim
  const handleAddRim = async () => {
    try {
      if (!profile?.id) {
        showErrorToast("User not found");
        return;
      }

      // Get business ID
      const { data: businessData } = await supabase
        .from("businesses")
        .select("id")
        .eq("owner_id", profile.id)
        .single();

      if (!businessData) {
        showErrorToast("Business not found");
        return;
      }

      // Validate required fields
      if (!formData.brand || !formData.model || !formData.size || formData.price === undefined) {
        showErrorToast("Please fill in all required fields");
        return;
      }

      const { error: insertError } = await supabase
        .from("rim_inventory")
        .insert({
          business_id: businessData.id,
          brand: formData.brand,
          model: formData.model,
          size: formData.size,
          price: formData.price,
          width: formData.width,
          diameter: formData.diameter,
          rim_offset: formData.rim_offset,
          bolt_pattern: formData.bolt_pattern,
          pcd: formData.pcd,
          center_bore: formData.center_bore,
          finish: formData.finish,
          material: formData.material,
          quantity: formData.quantity || 0,
          image_url: formData.image_url,
          gallery: formData.gallery,
          features: formData.features,
          description: formData.description,
        });

      if (insertError) {
        showErrorToast("Failed to add rim");
        console.error("Insert error:", insertError);
        return;
      }

      showSuccessToast("Rim added successfully");
      setShowAddForm(false);
      resetForm();
      fetchInventory();
    } catch (err) {
      showErrorToast("An error occurred");
      console.error("Add rim error:", err);
    }
  };

  // Update rim
  const handleUpdateRim = async (id: string) => {
    try {
      const rimToUpdate = inventory.find(r => r.id === id);
      if (!rimToUpdate) return;

      const { error: updateError } = await supabase
        .from("rim_inventory")
        .update(formData)
        .eq("id", id);

      if (updateError) {
        showErrorToast("Failed to update rim");
        console.error("Update error:", updateError);
        return;
      }

      showSuccessToast("Rim updated successfully");
      setEditingId(null);
      resetForm();
      fetchInventory();
    } catch (err) {
      showErrorToast("An error occurred");
      console.error("Update rim error:", err);
    }
  };

  // Delete rim
  const handleDeleteRim = async (id: string) => {
    if (!confirm("Are you sure you want to delete this rim?")) return;

    try {
      const { error: deleteError } = await supabase
        .from("rim_inventory")
        .delete()
        .eq("id", id);

      if (deleteError) {
        showErrorToast("Failed to delete rim");
        console.error("Delete error:", deleteError);
        return;
      }

      showSuccessToast("Rim deleted successfully");
      fetchInventory();
    } catch (err) {
      showErrorToast("An error occurred");
      console.error("Delete rim error:", err);
    }
  };

  const handleOpenVideoModal = (item: RimInventoryItem) => {
    setVideoTargetItem(item);
    setVideoModalOpen(true);
  };

  const handleSaveRimVideos = async (
    videoUrl: string | null,
    _videoType?: string,
    additionalVideos?: string[]
  ) => {
    if (!videoTargetItem) {
      throw new Error("No rim selected for video upload");
    }

    const normalizeUniqueUrls = (urls: Array<string | null | undefined>) => {
      const seen = new Set<string>();
      const result: string[] = [];

      urls.forEach((url) => {
        if (typeof url !== "string") {
          return;
        }

        const trimmed = url.trim();
        if (!trimmed || seen.has(trimmed)) {
          return;
        }

        seen.add(trimmed);
        result.push(trimmed);
      });

      return result;
    };

    let normalizedPrimary = typeof videoUrl === "string" && videoUrl.trim().length > 0
      ? videoUrl.trim()
      : null;
    let normalizedAdditional = normalizeUniqueUrls(additionalVideos ?? []);

    if (normalizedPrimary) {
      normalizedAdditional = normalizedAdditional.filter((url) => url !== normalizedPrimary);
    }

    if (!normalizedPrimary && normalizedAdditional.length > 0) {
      normalizedPrimary = normalizedAdditional[0];
      normalizedAdditional = normalizedAdditional.slice(1);
    }

    const { error: updateError } = await supabase
      .from("rim_inventory")
      .update({
        video_url: normalizedPrimary,
        videos: normalizedAdditional
      } as any)
      .eq("id", videoTargetItem.id);

    if (updateError) {
      throw updateError;
    }

    showSuccessToast("Video saved to rim listing");
    setVideoModalOpen(false);
    setVideoTargetItem(null);
    await fetchInventory();
  };

  // Start editing
  const handleEdit = (rim: RimInventoryItem) => {
    setEditingId(rim.id);
    setFormData({ ...rim });
  };

  // Cancel edit/add
  const handleCancel = () => {
    setEditingId(null);
    setShowAddForm(false);
    resetForm();
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      brand: "",
      model: "",
      size: "",
      width: null,
      diameter: null,
      rim_offset: null,
      bolt_pattern: "",
      pcd: null,
      center_bore: null,
      finish: "",
      material: "Alloy",
      price: 0,
      quantity: 0,
      image_url: "",
      video_url: "",
      videos: [],
      features: [],
      description: "",
    });
  };

  // Filter inventory
  const filteredInventory = inventory.filter(rim =>
    rim.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rim.model.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rim.size.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rim.finish?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex h-screen">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            <p className="mt-4 text-gray-600">Loading rim inventory...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      
      <div className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Rim Inventory Management</h1>
            <p className="text-gray-600">Manage your rim and wheel products</p>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-red-700">{error}</p>
            </div>
          )}

          {/* Action Bar */}
          <div className="bg-white rounded-xl shadow-lg p-4 mb-6 flex flex-col sm:flex-row gap-4 justify-between items-center">
            <input
              type="text"
              placeholder="Search rims..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full sm:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            
            <button
              onClick={() => setShowAddForm(true)}
              className="w-full sm:w-auto bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              + Add New Rim
            </button>
          </div>

          {/* Add/Edit Form */}
          {(showAddForm || editingId) && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-xl shadow-lg p-6 mb-6"
            >
              <h2 className="text-xl font-bold text-gray-900 mb-4">
                {editingId ? "Edit Rim" : "Add New Rim"}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                {/* Brand */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Brand <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="brand"
                    value={formData.brand}
                    onChange={handleInputChange}
                    placeholder="e.g., BBS, OZ Racing, Enkei"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Model */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Model <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleInputChange}
                    placeholder="e.g., CH-R, RPF1"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Diameter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Diameter (inches)
                  </label>
                  <input
                    type="number"
                    step="1"
                    value={formData.diameter || ""}
                    onChange={(e) => handleNumberChange("diameter", e.target.value)}
                    placeholder="e.g., 19"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Width */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Width (inches)
                  </label>
                  <input
                    type="number"
                    step="0.5"
                    value={formData.width || ""}
                    onChange={(e) => handleNumberChange("width", e.target.value)}
                    placeholder="e.g., 8.5"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Size Display */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Size Display <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="size"
                    value={formData.size}
                    onChange={handleInputChange}
                    placeholder='e.g., 19" x 8.5"'
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Offset */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Offset (ET)
                  </label>
                  <input
                    type="number"
                    value={formData.rim_offset || ""}
                    onChange={(e) => handleNumberChange("rim_offset", e.target.value)}
                    placeholder="e.g., 35"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Bolt Pattern */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Bolt Pattern
                  </label>
                  <input
                    type="text"
                    name="bolt_pattern"
                    value={formData.bolt_pattern || ""}
                    onChange={handleInputChange}
                    placeholder="e.g., 5x112"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* PCD */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    PCD (mm)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.pcd || ""}
                    onChange={(e) => handleNumberChange("pcd", e.target.value)}
                    placeholder="e.g., 112.0"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Center Bore */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Center Bore (mm)
                  </label>
                  <input
                    type="number"
                    step="0.1"
                    value={formData.center_bore || ""}
                    onChange={(e) => handleNumberChange("center_bore", e.target.value)}
                    placeholder="e.g., 66.6"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Finish */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Finish
                  </label>
                  <input
                    type="text"
                    name="finish"
                    value={formData.finish || ""}
                    onChange={handleInputChange}
                    placeholder="e.g., Gloss Black, Chrome"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Material */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Material
                  </label>
                  <select
                    name="material"
                    value={formData.material || "Alloy"}
                    onChange={handleInputChange}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="Alloy">Alloy</option>
                    <option value="Steel">Steel</option>
                    <option value="Forged">Forged</option>
                    <option value="Carbon Fiber">Carbon Fiber</option>
                  </select>
                </div>

                {/* Price */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price (R) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    value={formData.price || ""}
                    onChange={(e) => handleNumberChange("price", e.target.value)}
                    placeholder="e.g., 8500"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Quantity */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Quantity in Stock
                  </label>
                  <input
                    type="number"
                    value={formData.quantity || 0}
                    onChange={(e) => handleNumberChange("quantity", e.target.value)}
                    placeholder="e.g., 12"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Image URL */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Image URL
                  </label>
                  <input
                    type="text"
                    name="image_url"
                    value={formData.image_url || ""}
                    onChange={handleInputChange}
                    placeholder="https://example.com/rim-image.jpg"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Features */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Features (comma-separated)
                  </label>
                  <input
                    type="text"
                    value={formData.features?.join(', ') || ""}
                    onChange={(e) => handleFeaturesChange(e.target.value)}
                    placeholder="e.g., Lightweight, Racing, 2-Piece"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                {/* Description */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description
                  </label>
                  <textarea
                    name="description"
                    value={formData.description || ""}
                    onChange={handleInputChange}
                    rows={3}
                    placeholder="Optional description..."
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>

              {/* Form Actions */}
              <div className="flex gap-4">
                <button
                  onClick={editingId ? () => handleUpdateRim(editingId) : handleAddRim}
                  className="bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 transition-colors font-medium"
                >
                  {editingId ? "Update Rim" : "Add Rim"}
                </button>
                <button
                  onClick={handleCancel}
                  className="bg-gray-500 text-white px-6 py-2 rounded-lg hover:bg-gray-600 transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          )}

          {/* Inventory List */}
          <div className="bg-white rounded-xl shadow-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Image
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Video
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Brand & Model
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Specs
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Price
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stock
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredInventory.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                        {searchTerm ? "No rims found matching your search" : "No rims in inventory. Click 'Add New Rim' to get started."}
                      </td>
                    </tr>
                  ) : (
                    filteredInventory.map((rim) => (
                      <tr key={rim.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          {rim.image_url ? (
                            <img
                              src={rim.image_url}
                              alt={`${rim.brand} ${rim.model}`}
                              className="w-16 h-16 object-cover rounded"
                            />
                          ) : (
                            <div className="w-16 h-16 bg-gray-200 rounded flex items-center justify-center text-2xl">
                              🛞
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {rim.video_url || (rim.videos && rim.videos.length > 0) ? (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-800 font-medium">
                              Video added
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400">No video</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-medium text-gray-900">{rim.brand}</div>
                          <div className="text-sm text-gray-500">{rim.model}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {rim.size}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">
                          <div>{rim.bolt_pattern && `${rim.bolt_pattern}`}</div>
                          <div>{rim.rim_offset && `ET${rim.rim_offset}`}</div>
                          <div className="text-xs">{rim.finish}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          R {rim.price.toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 rounded text-xs font-medium ${
                            rim.quantity === 0 
                              ? 'bg-red-100 text-red-800' 
                              : rim.quantity <= 3 
                                ? 'bg-yellow-100 text-yellow-800' 
                                : 'bg-green-100 text-green-800'
                          }`}>
                            {rim.quantity} in stock
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                          <button
                            onClick={() => handleEdit(rim)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleOpenVideoModal(rim)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            Manage Video
                          </button>
                          <button
                            onClick={() => handleDeleteRim(rim.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Summary */}
          {filteredInventory.length > 0 && (
            <div className="mt-6 bg-white rounded-xl shadow-lg p-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-blue-600">{filteredInventory.length}</div>
                  <div className="text-sm text-gray-600">Total Rims</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-600">
                    {filteredInventory.reduce((sum, rim) => sum + rim.quantity, 0)}
                  </div>
                  <div className="text-sm text-gray-600">Total Stock</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-purple-600">
                    R {filteredInventory.reduce((sum, rim) => sum + (rim.price * rim.quantity), 0).toLocaleString()}
                  </div>
                  <div className="text-sm text-gray-600">Inventory Value</div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <VideoUploadModal
        isOpen={videoModalOpen}
        onClose={() => {
          setVideoModalOpen(false);
          setVideoTargetItem(null);
        }}
        onSubmit={handleSaveRimVideos}
        productName={videoTargetItem ? `${videoTargetItem.brand} ${videoTargetItem.model}` : "Rim"}
        existingPrimaryVideoUrl={videoTargetItem?.video_url ?? null}
        existingAdditionalVideos={videoTargetItem?.videos ?? []}
        allowMultiple={true}
        maxVideos={5}
      />
    </div>
  );
};

export default RimInventoryPage;
